import { Alert, Button, Descriptions, Input, Table } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  tuttiComputerApi,
  tuttiComputerErrorMessage,
} from '@/services/tuttiComputerApi';
import { computerExecutionDiagnosticFailure } from '@/services/tuttiComputerContract';
import type {
  ComputerExecutionDiagnosticPhase,
  ComputerExecutionDiagnostics,
} from '@/types';
import { computerDiagnosticPhaseLabel } from './computerDiagnosticPresentation';

interface Props {
  computerId: string;
  sessionId: string;
  turnId: string;
  onSessionIdChange: (value: string) => void;
  onTurnIdChange: (value: string) => void;
}

export function ComputerExecutionDiagnosticsSection({
  computerId,
  sessionId,
  turnId,
  onSessionIdChange,
  onTurnIdChange,
}: Props) {
  const generation = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] =
    useState<ComputerExecutionDiagnostics | null>(null);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const queryExecution = async () => {
    const requestGeneration = ++generation.current;
    setLoading(true);
    setError('');
    setDiagnostics(null);
    try {
      const result = await tuttiComputerApi.getExecutionDiagnostics(
        computerId,
        {
          sessionId: sessionId.trim() || undefined,
          turnId: turnId.trim() || undefined,
        },
      );
      if (generation.current === requestGeneration) setDiagnostics(result);
    } catch (queryError) {
      if (generation.current === requestGeneration) {
        const failure = computerExecutionDiagnosticFailure(queryError);
        setError(
          failure === 'ambiguous_turn'
            ? '此 Turn ID 匹配多个 Session，请同时填写 Session ID 后重新查询。'
            : failure === 'not_found'
              ? '未找到属于此 Computer 的 Session 或 Turn，请检查定位信息。'
              : tuttiComputerErrorMessage(queryError, '执行诊断查询失败'),
        );
      }
    } finally {
      if (generation.current === requestGeneration) setLoading(false);
    }
  };

  return (
    <section className="space-y-3">
      <h2 className="font-semibold">执行诊断</h2>
      <p className="text-sm text-muted-foreground">
        输入 Session 或 Turn ID 查看执行状态。未记录或已轮转的阶段显示缺失。
        采集诊断包时会附带此处的定位信息。
      </p>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!loading && (sessionId.trim() || turnId.trim())) {
            void queryExecution();
          }
        }}
      >
        <label className="min-w-60 flex-1 text-sm" htmlFor="diagnostic-session">
          Session ID
          <Input
            id="diagnostic-session"
            value={sessionId}
            onChange={(event) => onSessionIdChange(event.target.value)}
            allowClear
          />
        </label>
        <label className="min-w-60 flex-1 text-sm" htmlFor="diagnostic-turn">
          Turn ID
          <Input
            id="diagnostic-turn"
            value={turnId}
            onChange={(event) => onTurnIdChange(event.target.value)}
            allowClear
          />
        </label>
        <Button
          htmlType="submit"
          loading={loading}
          disabled={!sessionId.trim() && !turnId.trim()}
        >
          查询执行
        </Button>
      </form>
      {error ? <Alert type="error" showIcon title={error} /> : null}
      {diagnostics ? (
        <div className="space-y-3" aria-live="polite">
          <Descriptions
            size="small"
            column={2}
            items={[
              {
                key: 'session',
                label: 'Session',
                children: diagnostics.sessionId || '-',
              },
              {
                key: 'turn',
                label: 'Turn',
                children: diagnostics.turnId || '-',
              },
              {
                key: 'state',
                label: '执行状态',
                children: diagnostics.state || '缺失',
              },
              {
                key: 'command',
                label: '命令状态',
                children: diagnostics.commandState || '缺失',
              },
            ]}
          />
          {diagnostics.roomRoute ? (
            diagnostics.roomRoute.state === 'missing' ? (
              <Alert
                type="warning"
                showIcon
                title="Room 文件路由观测缺失"
                description={diagnostics.roomRoute.reason}
              />
            ) : (
              <Descriptions
                size="small"
                bordered
                column={2}
                title="Room 文件路由"
                items={[
                  {
                    key: 'room',
                    label: 'Room ID',
                    children: diagnostics.roomRoute.roomId || '-',
                  },
                  {
                    key: 'registered',
                    label: '路由注册',
                    children: diagnostics.roomRoute.registered
                      ? '已注册'
                      : '未注册',
                  },
                  {
                    key: 'socket',
                    label: '数据通道',
                    children: diagnostics.roomRoute.dataSocketPresent
                      ? '存在'
                      : '不存在',
                  },
                  {
                    key: 'tmp',
                    label: 'Sandbox tmp',
                    children: diagnostics.roomRoute.sandboxTmpRegistered
                      ? '已注册'
                      : '未注册',
                  },
                  {
                    key: 'home',
                    label: 'Sandbox Home',
                    children: diagnostics.roomRoute.sandboxHomeRegistered
                      ? '已注册'
                      : '未注册',
                  },
                  {
                    key: 'homeview',
                    label: 'Home view',
                    children: diagnostics.roomRoute.homeviewRegistered
                      ? '已注册'
                      : '未注册',
                  },
                ]}
              />
            )
          ) : null}
          <Table<ComputerExecutionDiagnosticPhase>
            rowKey="phase"
            size="small"
            pagination={false}
            dataSource={diagnostics.phases}
            locale={{ emptyText: '没有可用的阶段记录' }}
            columns={[
              {
                title: '阶段',
                dataIndex: 'phase',
                render: computerDiagnosticPhaseLabel,
              },
              {
                title: '耗时',
                render: (_value, phase) =>
                  phase.recorded && phase.durationMs !== ''
                    ? `${phase.durationMs} ms`
                    : '缺失',
              },
              { title: '详情', dataIndex: 'detail' },
            ]}
          />
        </div>
      ) : null}
    </section>
  );
}
